// OWNER: TELEPHONY agent. Module-specific request/response types for the telephony routes
// (/api/voice, /api/numbers, /api/recordings, /api/inbound, /api/calls/:id/drop). Core shared types live in api.ts.
import type { Country, GateResult, ISODate, LineType, NumberStatus } from './api';

// ---------- voice (GET /api/voice/token) ----------

export interface VoiceTokenResponse {
  token: string;               // Twilio Voice access token (JWT), pass to new Device(token)
  identity: string;            // the user's twilio identity, e.g. 'u7'
  expiresAt: ISODate;          // refresh before this (Device emits tokenWillExpire)
}

/** Custom parameters on an inbound call to the browser (call.customParameters in @twilio/voice-sdk). */
export const INBOUND_CALL_PARAMS = {
  callId: 'inboundCallId',     // calls.id of the inbound row (string)
  businessId: 'businessId',    // businesses.id as a string, or '' when the caller is not a known business
  businessName: 'businessName',// business name, or '' when unknown
} as const;

// ---------- numbers (admin) ----------

export interface AvailableNumber {
  e164: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;       // state / province code as Twilio reports it ('GA', 'ON')
  postalCode: string | null;
  country: Country;
  addressRequirements: string; // 'none' | 'any' | 'local' | 'foreign'
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
}

export interface NumberBuyRequest {
  e164: string;
  purpose: 'outbound' | 'main';
  metro?: string;
  country?: Country;           // pass AvailableNumber.country; inferred from the area code when omitted
  region?: string;             // pass AvailableNumber.region; used for same-state caller ID matching
}

export interface NumberImportRequest {
  e164: string;
  purpose?: 'outbound' | 'main'; // default 'outbound'
  metro?: string;
  country?: Country;
  region?: string;
}

export interface NumberPatchRequest {
  status?: NumberStatus;
  note?: string;               // REQUIRED when status changes (goes into the ledger)
  metro?: string | null;
  region?: string | null;
  notes?: string | null;
  cnamSet?: boolean;
  fcrRegisteredAt?: ISODate | null;
  hiyaRegisteredAt?: ISODate | null;
  retiredReason?: string | null; // required (or note) when status -> retired
}

export type NumberEventKind =
  | 'purchased' | 'imported' | 'registered' | 'flag_alert' | 'flagged' | 'remediation' | 'rested' | 'reactivated'
  | 'retired' | 'test_call' | 'note';

/** Kinds an admin may add by hand via POST /api/numbers/:id/events (status kinds come from PATCH). */
export const MANUAL_NUMBER_EVENT_KINDS = ['registered', 'remediation', 'test_call', 'note'] as const;
export type ManualNumberEventKind = (typeof MANUAL_NUMBER_EVENT_KINDS)[number];

export interface NumberEvent {
  id: number;
  numberId: number;
  kind: NumberEventKind;
  note: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: ISODate;
}

export interface NumberEventCreateRequest { kind: ManualNumberEventKind; note: string }

export interface NumberDailyRow {
  day: string;                 // 'YYYY-MM-DD' in OPS_TZ
  dials: number;
  answered: number;
  shortCalls: number;
  connects: number;
  sip603: number;
  sip608: number;
  failed: number;
}

// ---------- SHAKEN/STIR attestation self-test (RUNBOOK §2 T1) ----------
// One of our numbers calls another of ours; /twilio/inbound does not ring anyone. It logs a number_events 'test_call'
// row on the calling number (created_by NULL, note `StirVerstat=<value>`), reads the result out and hangs up.
// OurNumber.lastAttestation.level is that value: Twilio's StirVerstat exactly (e.g. 'TN-Validation-Passed-A'), or
// 'none' when Twilio sent no StirVerstat (the call carried no SHAKEN PASSporT).

export const ATTESTATION_NOTE_PREFIX = 'StirVerstat=';
/** The only result that means carriers see our calls as fully verified. */
export const ATTESTATION_TARGET = 'TN-Validation-Passed-A';

/** A StirVerstat value in plain words (the self-test reads this out; the Numbers page may show it). Values from
 *  twilio.com/docs/voice/trusted-calling-with-shakenstir, including the -Diverted and -Passthrough variants. */
export function attestationInWords(level: string | null | undefined): string {
  const v = String(level ?? '').trim();
  if (!v || v.toLowerCase() === 'none') return 'no attestation received';
  const m = /^TN-Validation-(Passed|Failed)(?:-([ABC]))?(?:-(Diverted|Passthrough))?$/.exec(v);
  if (m) {
    const [, result, grade, via] = m;
    let words: string;
    if (result === 'Passed') {
      words = grade === 'A' ? 'level A, fully verified'
        : grade === 'B' ? 'level B, partly verified'
        : grade === 'C' ? 'level C, the lowest level'
        : 'verified, level not given';
    } else {
      words = grade ? `level ${grade}, but verification failed` : 'verification failed';
    }
    if (via === 'Diverted') words += ', on a forwarded call';
    if (via === 'Passthrough') words += ', passed through';
    return words;
  }
  if (v === 'No-TN-Validation') return 'no valid signature';
  if (v.toUpperCase() === 'NULL') return 'verification failed';
  return 'an unrecognized result';
}

// ---------- inbound voicemail box ----------

export interface InboundVoicemail {
  id: number;
  numberId: number | null;
  numberE164: string | null;   // our number they called
  fromE164: string;
  businessId: number | null;
  businessName: string | null;
  durationSec: number | null;
  createdAt: ISODate;
  handledAt: ISODate | null;
  handledBy: number | null;
  handledByName: string | null;
  audioUrl: string;            // same-origin, authenticated: /api/inbound/voicemails/:id/audio (audio/mpeg)
  assigneeId: number | null;   // holder of this voicemail's task (else the user the call rang); null = any admin
  canMarkHandled: boolean;     // admins, or the assignee; others get 403 from POST /:id/handled (hide the button)
}

// ---------- voicemail-drop recordings ----------

export const RECORDING_CHECKLIST = [
  { key: 'legalNameFirst', label: 'Says their name and "Valistix" at the start' },
  { key: 'callbackNumber', label: 'States a callback number' },
  { key: 'under30s', label: 'Under 30 seconds' },
  { key: 'humanVoice', label: "The setter's own human voice (no AI, no synthetic voice)" },
  { key: 'noClaims', label: 'No results promises, stats, health or cannabis-effect claims' },
] as const;
export type RecordingChecklistKey = (typeof RECORDING_CHECKLIST)[number]['key'];

export interface RecordingReviewRequest {
  status: 'approved' | 'rejected';
  reviewNote?: string;
  checklist: Record<RecordingChecklistKey, boolean>; // approval requires every item true
}

/** Upload limits enforced by POST /api/recordings (multipart fields: file, situation, campaignId?, scriptText, humanVoiceAttested). */
export const RECORDING_LIMITS = { maxBytes: 2 * 1024 * 1024, maxSeconds: 30, types: ['audio/wav', 'audio/mpeg'] } as const;

// ---------- voicemail drop (POST /api/calls/:id/drop) ----------

/** The 409 gate_blocked `details` of a refused drop: the GateResult, plus what the press-time line check found when the
 *  route ran one (PJ 2026-10-03), so the dialer can show the fresh line type without reloading the lead. */
export interface DropRefusal extends GateResult {
  lineCheck?: { lineType: LineType; lookupAt: ISODate };
}

/** PJ 2026-10-03: the drop route looks the line type up itself when V is pressed if the stored one is missing, 'unknown'
 *  or older than 14 days. So before the press, `drop_lookup_stale` never blocks, and `drop_line_type` blocks only when
 *  a lookup already said cell / internet VoIP / toll-free. Used for the queue's precomputed dropGate and the Drop VM
 *  button; a refusal the server sends after its own lookup is shown as is. */
export function dropGateBeforeLineCheck(gate: GateResult, business: { lineType?: LineType | null }, opts: { lookupPossible?: boolean } = {}): GateResult {
  // PJ 2026-10-23: a call to a number other than the business's own (the owner's line, the store's Google line) is never
  // looked up, so its drop_line_type stays (lookupPossible false).
  if (opts.lookupPossible === false) return gate;
  const typeUnknown = !business.lineType || business.lineType === 'unknown';
  const reasons = gate.reasons.filter((r) => r.code !== 'drop_lookup_stale' && !(r.code === 'drop_line_type' && typeUnknown));
  if (reasons.length === gate.reasons.length) return gate;
  return reasons.length ? { ...gate, ok: false, reasons } : { ok: true, reasons: [] };
}

// ---------- recording with the prospect's yes (POST /api/calls/:id/record-consent; PJ 2026-10-20) ----------
// Behind the admin setting consent_recording_enabled (default off). Only on a connected outbound call the recording rule
// skipped for all-party consent (CallPrepareResponse.consentRecording). Refusals are 409s with one plain sentence:
// consent_recording_off, not_outbound, consent_already_started, already_recording, not_consent_call, call_not_live;
// 502 recording_start_failed when Twilio didn't start it (the call goes on, unrecorded). POST .../record-consent/stop
// stops it when they take the yes back.

/** What the setter asks first, and says once it is on (word for word in the confirm, the guide and the playbook). */
export const CONSENT_RECORDING_ASK = 'Mind if I record the rest of this call so our team has the details right?';
export const CONSENT_RECORDING_ON = "Recording's on now, that's still okay with you?";

/** The whole confirm, word for word (the call bar's dialog; the guide and the playbook say the same). */
export const CONSENT_RECORDING_CONFIRM = `Ask first: "${CONSENT_RECORDING_ASK}" Start only after they say yes. Once it's on, say it out loud: "${CONSENT_RECORDING_ON}"`;

export interface ConsentRecordingResponse {
  ok: true;
  callId: number;
  policy: 'record_consent';           // calls.recording_policy from now on
  startedAt: string;                  // when the yes was pressed (audited call.recording_consent {userId, at})
}

/** POST /api/calls/:id/record-consent/stop: they took the yes back. 409 not_recording when it isn't on; 502
 *  recording_stop_failed when Twilio didn't stop it (hang up then). What was recorded with their yes is kept. */
export interface ConsentRecordingStopResponse { ok: true; callId: number; stoppedAt: string }
